import { Delete } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { cloneDeep, get, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import ScrollToTop from 'src/components/layout/ScrollToTop';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import LoadingIconOnly from 'src/components/ui-component/loading/LoadingIconOnly';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import MuiSnackbar from 'src/components/ui-component/mui-snackbar/MuiSnackbar';
import { useAuthentication } from 'src/context/AuthContext';
import GetRecord from 'src/lib/api/GetRecord';
import { PostRecord } from 'src/lib/api/PostRecord';
import PutRecord from 'src/lib/api/PutRecord';
import { APIS } from 'src/lib/constants/apis';
import { AUTH_TOKEN_DATA, NOT_FOUND_ERROR_MESSAGE } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { getFieldNamefromDataPath, removeDataKeys, removeNullDeep } from 'src/utils/helper';
import BankDatenValidator from './BankDataValidator';

// import schemas
import OrganisationSchema from 'src/lib/paths/organisation_FULL_Paths.json';
let P = OrganisationSchema.attributes;

const OrganisationBankDaten: FC<{}> = () => {
	const { authenticatedData } = useAuthentication();
	const [loading, setLoading] = useState<boolean>(true);

	const [formDataChanged, setFormDataChanged] = useState(false);
	const [updateSuccess, setUpdateSuccess] = useState<boolean | null>(null);
	const [wsError, setWsError] = useState<boolean>(false);
	const [organisationData, setOrganisationData] = useState<Record<string, any>>({});
	const [formErrors, setFormErrors] = useState<Record<string, any>>({});

	// Update Form Input Values
	const handleInputChange = (path: string, value: any) => {
		setFormDataChanged(true);
		setUpdateSuccess(null);
		setWsError(false);
		let organisationDataCopy = cloneDeep(organisationData);
		set(organisationDataCopy, path, value);
		setOrganisationData(organisationDataCopy);
	};

	// Handle Add/Delete Bankdaten
	const handleAddBankdaten = () => {
		let orgaCopy = cloneDeep(organisationData);
		const currentBankdaten = get(organisationData, P.Bankdaten.path, []);
		set(orgaCopy, `${P.Bankdaten.path}.${currentBankdaten.length}`, {
			Bezeichnung: '',
			Bankdaten_Geldininstitut: '',
			Bankdaten_Kontoinhaber: '',
			Bankdaten_IBAN: '',
			Bankdaten_BIC: '',
		});
		setOrganisationData(orgaCopy);
		setFormDataChanged(true);
	};
	const handleDeleteBankdaten = (index: number) => {
		let orgaCopy = cloneDeep(organisationData);
		const allBankdatenCopy = get(orgaCopy, P.Bankdaten.path, []);
		set(
			orgaCopy,
			P.Bankdaten.path,
			allBankdatenCopy.filter((_: any, i: number) => i !== index),
		);
		setOrganisationData(orgaCopy);
		setFormDataChanged(true);
	};

	// Validating Form Data before sending update request
	const validateOrganisationData = () => {
		let errorObject: any = {};
		let orgaCopy = cloneDeep(organisationData);

		let errs = BankDatenValidator(orgaCopy).validate(orgaCopy);
		for (let error of errs) {
			set(errorObject, error.path, get(error, 'message'));
		}

		setFormErrors(errorObject);
		return Object.keys(errorObject).length === 0;
	};

	// Handle submit
	const handleUpdateOrganisationData = async () => {
		if (validateOrganisationData()) {
			try {
				setLoading(true);
				let res = await PutRecord(APIS.Organisation, get(authenticatedData, AUTH_TOKEN_DATA.organisationId), {
					data: get(organisationData, 'attributes'),
				});
				if (get(res, 'data.id')) {
					// Sending Pushnotification
					const pushNotificationData = [{ api: APIS.Organisation, id: get(res, 'data.id') }];
					await PostRecord(APIS.PushNotificaiton, { data: { entities: pushNotificationData } });
					setUpdateSuccess(true);
				}
			} catch (err: any) {
				console.error('ERROR: Updating Organisation Data ', err);
			} finally {
				setLoading(false);
				setFormDataChanged(false);
			}
		}
	};

	// Fetch Organisation Data on component mount
	useEffect(() => {
		const fetchOrganisation = async () => {
			setLoading(true);
			try {
				let res = await GetRecord(APIS.Organisation, get(authenticatedData, AUTH_TOKEN_DATA.organisationId), [
					getFieldNamefromDataPath(P.Bankdaten.path),
				]);
				setOrganisationData(removeNullDeep(removeDataKeys(get(res, 'data', {}))));
			} catch (err) {
				console.error('ERROR Fetching Organisation ', err);
				setWsError(true);
			} finally {
				setLoading(false);
			}
		};
		fetchOrganisation();
	}, [authenticatedData]);

	if (loading) {
		return <LoadingIconOnly />;
	}

	return (
		<>
			<MuiSnackbar showSnackbar={wsError} closeSnackbar={() => setWsError(false)} alertMessage={NOT_FOUND_ERROR_MESSAGE} error={wsError} />

			<Typography variant='h3' component='h2'>
				Bankverbindung
			</Typography>

			<Typography variant='body1' sx={{ mb: 2 }}>
				Bitte legen Sie hier mindestens ein Konto an. Falls Sie mehreren Konten anlegen, können Sie diese in den Förderanträgen auswählen und
				zuordnen. Geben Sie eingängige Bezeichnungen ein, z.B. den Namen der Bank, Geschäftskonto ABC oder Projekt XY.
			</Typography>

			{updateSuccess && (
				<>
					<ScrollToTop />
					<MUIAlert infoType={'success'} title='Ihre Bankdaten wurden erfolgreich aktualisiert.' message={''} />
				</>
			)}

			{get(organisationData, P.Bankdaten.path, []).map((_: any, i: number) => {
				return (
					<Stack key={i} direction={'row'} alignContent={'center'} justifyContent={'flex-start'} alignItems={'flex-start'} sx={{ mt: 2 }}>
						<Box sx={{ width: '95%', border: '2px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
							<CreateForm
								formStruc={mapFormStrucBankDaten(formStrucBankDaten(), i)}
								formValues={organisationData}
								formErrors={formErrors}
								updateFormValuesFunc={handleInputChange}
								api={APIS.Organisation}
							/>
						</Box>
						<IconButton onClick={() => handleDeleteBankdaten(i)} sx={{ mt: 1 }}>
							<Delete />
						</IconButton>
					</Stack>
				);
			})}

			<Stack direction={{ xs: 'column', md: 'row' }} justifyContent={'space-between'} spacing={2} sx={{ mt: '2rem' }}>
				<Button onClick={handleAddBankdaten} size={'small'} endIcon={<AddIcon />}>
					Konto hinzufügen
				</Button>
				<Button disabled={!formDataChanged} onClick={handleUpdateOrganisationData}>
					Aktualisieren
				</Button>
			</Stack>
		</>
	);
};
export default OrganisationBankDaten;

const formStrucBankDaten = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [{ ...P.Bankdaten.schema.attributes.Bezeichnung, label: 'Bezeichnung des Kontos' }],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{ fields: [P.Bankdaten.schema.attributes.Bankdaten_Geldininstitut, P.Bankdaten.schema.attributes.Bankdaten_IBAN] },
		{ fields: [P.Bankdaten.schema.attributes.Bankdaten_Kontoinhaber, { ...P.Bankdaten.schema.attributes.Bankdaten_BIC }] },
	];
	return struc;
};

const mapFormStrucBankDaten = (formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path: pathWithoutIndex.slice(0, P.Bankdaten.path.length) + `.${index}` + pathWithoutIndex.slice(P.Bankdaten.path.length),
				};
			}),
		};
	});
	return result;
};
